// src/Pages/Wizard.jsx
import { useState } from "react";
import { createCase, saveCase, submitCase } from "../api/cases";
import productsData from "../data/products.generated.json";
import { FaRegWindowClose } from "react-icons/fa";

import { generateSummary } from "../Components/Wizard/generateSummary";
import { useWizardEngine } from "../Components/Wizard/useWizardEngine";
import {
  buildLineSelectionUrl,
  generateCopyText,
  generateSingleProductText,
  selectionTextFrom,
} from "../Components/Wizard/copyText";
import { featureMatches } from "../Components/Wizard/matchers";

import Seo from "../Components/Seo";

const Wizard = () => {
  const products = productsData;
  const engine = useWizardEngine({
    products,
    caseApi: { createCase, saveCase, submitCase },
  });

  const getOptionLabel = (question, selectedValue) => {
    if (!selectedValue) return "未選択";

    const matchedOption = question.options.find((option) => {
      if (typeof option === "string") {
        return option === selectedValue;
      }

      return option.value === selectedValue;
    });

    if (!matchedOption) return selectedValue;

    return typeof matchedOption === "string" ? matchedOption : matchedOption.label;
  };

  const {
    questions,
    step,
    selectedOptions,
    filteredProducts,
    alternativeProducts,
    hasExact,
    hasAlternative,
    caseId,
    start,
    select,
    back,
    relaxConditions,
    reset,
  } = engine;

  const [copied, setCopied] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);

  const selectionText = selectionTextFrom({ questions, selectedOptions });
  const lineSelectionUrl = buildLineSelectionUrl({ selectionText });

  const currentList = filteredProducts.length > 0 ? filteredProducts : alternativeProducts;

  const isNoPreference = (value, label = "") => {
    return (
      value === "こだわらない" ||
      value === "any" ||
      value === "no_preference" ||
      value === "not_sure" ||
      value === "none" ||
      label === "こだわらない" ||
      label.includes("こだわらない")
    );
  };

  const openProductMail = ({ product, productText }) => {
    const textWithCaseId = caseId ? `受付ID : ${caseId}\n${productText}` : productText;

    const subject = `【CAMARAGO】商品相談：${product.productName}（${product.modelNumber}）`;

    const body = `以下をご入力のうえ、そのまま送信してください。
  
【お客様情報】
会社名：
お名前：
電話番号：
メールアドレス：
  
【ご相談内容】
この商品について詳しく相談したいです。
  
------------------------------
  
${textWithCaseId}
  
------------------------------
  
CAMARAGOからの商品相談メールです。`;

    navigator.clipboard.writeText(body).then(() => {
      const mailtoUrl = `mailto:contact@camarago.com?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;

      window.location.href = mailtoUrl;
    });
  };

  const calculateMatchInfo = (product) => {
    const targetQuestions = questions.filter((q) => {
      const selectedValue = selectedOptions[q.key];
      const selectedLabel = getOptionLabel(q, selectedValue);

      return selectedValue && !isNoPreference(selectedValue, selectedLabel);
    });

    if (targetQuestions.length === 0) {
      return {
        rate: 100,
        matchedCount: 0,
        targetCount: 0,
      };
    }

    const matchedCount = targetQuestions.filter((q) => {
      const selectedValue = selectedOptions[q.key];

      return featureMatches(product.features[q.key], selectedValue, q.key, product);
    }).length;

    return {
      rate: Math.round((matchedCount / targetQuestions.length) * 100),
      matchedCount,
      targetCount: targetQuestions.length,
    };
  };

  return (
    <div className="mx-auto w-11/12 my-20">
      <Seo
        pageTitle="診断ページ"
        description="CAMARAGO(カマラゴ)は、あなたに最適な防犯カメラ・監視カメラを直感的な選択形式で選べるサービスです。設置までもワンストップで提供します。"
        url="https://camarago.com"
      />
      {step === -1 ? (
        <div className="sm:w-1/2 m-auto text-center space-y-6 py-10 px-6 bg-white rounded-xl shadow">
          <h2 className="font-bold text-[#002b5b] text-[clamp(9px,8vw,15px)]">
            直感的でシンプルな選択を重ねるだけ。
          </h2>
          <img className="w-1/2 m-auto" src="/images/timeline.png" alt="" />
          <p className="text-gray-700 text-sm text-left leading-relaxed">
            カメラ選びでよくある「多すぎて決められない」という不安を、直感で進める選択肢形式でやさしく解決。使い方や重視したいことを選んでいくだけで、自然と自分に合ったモデルが導き出されるような設計になっています。
          </p>

          <button
            onClick={async () => {
              try {
                await start();
              } catch (e) {
                console.error(e);
                alert("サーバーに接続できませんでした。もう一度お試しください。");
              }
            }}
            className="bg-[#002b5b] text-white px-6 py-3 rounded-full hover:bg-[#003d7a] transition"
          >
            診断をはじめる
          </button>
        </div>
      ) : step < questions.length ? (
        <div className="sm:w-1/2 m-auto">
          <h2 className="text-xl font-bold text-center text-white">{questions[step].question}</h2>
          <img className="w-1/2 m-auto" src={questions[step].image} alt="" />

          <div className="grid gap-3">
            {questions[step].options.map((option, index) => {
              const optionLabel = typeof option === "string" ? option : option.label;
              const optionValue = typeof option === "string" ? option : option.value;

              return (
                <button
                  key={`${questions[step].key}-${optionValue}-${index}`}
                  className="w-full py-3 px-4 rounded-full bg-white text-[#002b5b] text-sm font-semibold border border-[#002b5b] hover:bg-[#002b5b] hover:text-white hover:border-white transition"
                  onClick={() => select(questions[step].key, optionValue)}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>

          <div className="w-full mt-8 sequencebar">
            <div className="bg-gray-200 border-2 border-black rounded-full h-6 relative overflow-hidden">
              <div
                className="bg-gray-300 h-6 rounded-full transition-all duration-300"
                style={{ width: `${(step / questions.length) * 100}%` }}
              />
              <div className="absolute top-0 left-0 w-full h-4 flex justify-between items-center px-2">
                {questions.map((_, index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full border-2 ${
                      index <= step ? "bg-[#002b5b] border-[#002b5b]" : "bg-white border-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6">
            <button
              onClick={back}
              className="bg-gray-400 text-white py-2 px-5 rounded hover:bg-gray-500 disabled:opacity-50"
              disabled={step === 0}
            >
              一つ戻る
            </button>

            <button
              onClick={() => setShowTipModal(true)}
              className="text-sm m-auto text-blue-100 underline hover:text-white "
            >
              質問の解説を見る
            </button>

            <button
              onClick={reset}
              className="bg-red-500 text-white py-2 px-5 rounded hover:bg-red-600"
            >
              最初に戻る
            </button>
          </div>

          {showTipModal && step < questions.length && (
            <div className="fixed inset-0 bg-[#002b5b] bg-opacity-75s flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-xl max-w-md w-[90%] shadow-xl relative">
                <h3 className="text-lg font-bold text-gray-800 mb-2">{questions[step].subtitle}</h3>

                <h4 className="text-sm font-bold text-gray-800 mb-1">解説</h4>
                <p className="text-sm leading-relaxed text-gray-700">
                  {questions[step].description || "この質問に関する補足説明が入ります。"}
                </p>

                <h4 className="text-sm font-bold text-gray-800 mt-3 mb-1">迷ったときは？</h4>
                <p className="text-sm leading-relaxed text-gray-700">
                  {questions[step].uncertain || "この質問に関する補足説明が入ります。"}
                </p>

                <button
                  onClick={() => setShowTipModal(false)}
                  className="absolute top-2 right-3 text-gray-400 hover:text-black text-xl"
                >
                  <FaRegWindowClose />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6 ">
          {!hasExact && !hasAlternative && (
            <div className="text-center text-white space-y-4 mb-8">
              <h2 className="text-xl font-bold">該当するカメラが見つかりませんでした</h2>
              <img className="w-1/2 md:w-1/5 m-auto" src="/images/sad-face.png" alt="" />

              <p>
                選んだ条件をもとにLINEでご相談いただければ、
                <br />
                スタッフが最適なご提案をいたします。
              </p>

              <button
                onClick={() => {
                  const textWithCaseId = `受付ID : ${caseId}　${selectionText}`;

                  navigator.clipboard.writeText(textWithCaseId).then(() => {
                    window.open(lineSelectionUrl, "_blank");
                  });
                }}
                className="block m-auto mt-4 bg-[#00B900] text-white py-3 px-6 text-lg hover:brightness-90 transition"
              >
                選択内容をコピーしてLINEで相談する
              </button>

              <button
                onClick={() => {
                  const subject = "【CAMARAGO】防犯カメラ選定の相談";

                  const body = `以下をご入力のうえ、そのまま送信してください。

【お客様情報】
会社名：
お名前：
電話番号：
メールアドレス：

【ご相談内容】
条件に合うカメラが見つからなかったため、最適なカメラについて相談したいです。

------------------------------

受付ID：${caseId ?? "未発行"}

【選択内容】
${selectionText}

------------------------------

CAMARAGOからの商品相談メールです。`;

                  navigator.clipboard.writeText(body).then(() => {
                    const mailtoUrl = `mailto:contact@camarago.com?subject=${encodeURIComponent(
                      subject
                    )}&body=${encodeURIComponent(body)}`;

                    window.location.href = mailtoUrl;
                  });
                }}
                className="block m-auto mt-3 bg-white text-[#002b5b] py-3 px-6 text-lg hover:bg-blue-50 transition"
              >
                選択内容をコピーしてメールで相談する
              </button>

              <button
                onClick={relaxConditions}
                className="hidden m-auto mt-4 bg-white hover:bg-yellow-50 text-black py-2 px-5  text-sm  transition"
              >
                条件をゆるめて再診断する
              </button>
            </div>
          )}

          {hasExact && (
            <div>
              <h2 className="text-[clamp(16px,8vw,24px)] font-bold text-center text-white">
                あなたにぴったりのカメラはこちらです！
              </h2>

              <img className="w-1/2 m-auto" src="/images/creative-process.png" alt="" />
            </div>
          )}

          {!hasExact && hasAlternative && (
            <div>
              <h2 className="text-[clamp(16px,8vw,24px)] font-bold text-center text-white">
                完全一致のカメラは見つかりませんでしたが、近い候補をご紹介します。
              </h2>

              <img className="w-1/2 m-auto" src="/images/user-statu.png" alt="" />
            </div>
          )}

          <p className="text-white sm:text-center">
            商品情報を控えておくことで、あとで問い合わせるときに迷わずスムーズに進められます。
          </p>

          {(filteredProducts.length > 0 || alternativeProducts.length > 0) && (
            <button
              onClick={() => {
                const textToCopy = generateCopyText({
                  products: currentList,
                  questions,
                  selections: selectedOptions,
                });
                navigator.clipboard
                  .writeText(textToCopy)
                  .then(() => {
                    alert("商品情報をコピーしました！そのまま問い合わせに使えます。");
                  })
                  .catch(() => {
                    alert("コピーに失敗しました。ブラウザの設定をご確認ください。");
                  });
              }}
              className="mt-6 bg-white text-[#002b5b] py-2 px-4 rounded-lg hover:bg-gray-100"
            >
              全ての商品情報を一括コピーする
            </button>
          )}

          {filteredProducts.length > 0 ? (
            <ul className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredProducts.map((product, i) => {
                const messageText = `商品名: ${product.productName}\n型番: ${product.modelNumber}\nこの商品が気になっているので、詳細について相談したいです。`;
                const lineMessageUrl = `https://line.me/R/oaMessage/@camarago/?${encodeURIComponent(
                  messageText
                )}`;

                const summary = generateSummary(product);

                return (
                  <li key={product.id} className="p-4 border rounded-xl shadow bg-white">
                    <img
                      src={`/images/${product.modelNumber}.jpg`}
                      alt={product.productName}
                      className="w-48 h-48 object-cover mx-auto hidden"
                    />
                    {product.makerName && (
                      <div className="mt-2 mb-2 inline-flex items-center rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold text-[#002b5b]">
                        {product.makerName}
                      </div>
                    )}

                    <p className="text-lg font-bold">
                      {product.productName} ({product.modelNumber})
                    </p>

                    <p className="text-gray-700 hidden">
                      {typeof product.price === "number"
                        ? `¥${product.price.toLocaleString()}`
                        : product.price}
                    </p>

                    <ul className="text-sm mt-3 space-y-1 bg-blue-50 rounded-lg p-3">
                      <li>
                        <span className="font-semibold">設置場所：</span>
                        {summary.mountType}
                      </li>
                      <li>
                        <span className="font-semibold">通信方法：</span>
                        {summary.connectivity}
                      </li>
                      <li>
                        <span className="font-semibold">録画方法：</span>
                        {summary.recordingMethod}
                      </li>
                      <li>
                        <span className="font-semibold">撮影範囲：</span>
                        {summary.lensType}
                      </li>
                      <li>
                        <span className="font-semibold">防水防塵：</span>
                        {summary.waterproofDustproof}
                      </li>
                      <li>
                        <span className="font-semibold">画質：</span>
                        {summary.resolution}
                      </li>
                      <li>
                        <span className="font-semibold">暗所性能：</span>
                        {summary.lowLightPerformance}
                      </li>
                      <li>
                        <span className="font-semibold">AI機能：</span>
                        <ul className="mt-1 ml-4 list-disc">
                          {summary.aiFeatures.map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      </li>
                    </ul>

                    <ul className="hidden text-sm mt-2 space-y-1">
                      {questions.map(({ key, question }) => (
                        <li key={key}>
                          {question}:{" "}
                          <span className="font-semibold">
                            {Array.isArray(product.features[key])
                              ? product.features[key].join("・")
                              : product.features[key]}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(messageText).then(() => {
                          window.open(lineMessageUrl, "_blank");
                        });
                      }}
                      className="block mt-3 bg-[#00B900] text-white py-1 px-3 rounded hover:brightness-90 text-sm"
                    >
                      LINEでこの商品を相談する
                    </button>

                    <small className="block pl-1 pt-2">
                      スマートフォン以外から閲覧されている場合、LINEでのお問合せが機能しない場合がございます。お問い合わせは以下よりメールでお願いいたします。
                    </small>

                    <button
                      onClick={() => {
                        const productText = generateSingleProductText({
                          product,
                          questions,
                          selections: selectedOptions,
                        });

                        openProductMail({ product, productText });
                      }}
                      className="mt-2 bg-white text-[#002b5b] border border-[#002b5b] py-1 px-3 rounded hover:bg-blue-50 text-sm block"
                    >
                      メールでこの商品を相談する
                    </button>

                    <button
                      onClick={() => {
                        const productText = generateSingleProductText({
                          product,
                          questions,
                          selections: selectedOptions,
                        });

                        const textWithCaseId = caseId
                          ? `受付ID : ${caseId}\n${productText}`
                          : productText;

                        navigator.clipboard.writeText(textWithCaseId).then(() => {
                          alert("この商品をコピーしました！");
                        });
                      }}
                      className="mt-2 bg-[#002b5b] text-white py-1 px-3 rounded hover:bg-blue-700 text-sm"
                    >
                      この商品情報をコピー
                    </button>
                  </li>
                );
              })}{" "}
            </ul>
          ) : (
            <>
              {alternativeProducts.length > 0 && (
                <>
                  <h3 className="text-lg font-bold mt-4 text-white">おすすめ候補</h3>
                  <ul className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {alternativeProducts.map((product) => {
                      const messageText = `商品名: ${product.productName}\n型番: ${product.modelNumber}\nこの商品が気になっているので、詳細について相談したいです。`;
                      const lineMessageUrl = `https://line.me/R/oaMessage/@camarago/?${encodeURIComponent(
                        messageText
                      )}`;

                      const summary = generateSummary(product);

                      return (
                        <li key={product.id} className="p-4 border rounded-xl shadow bg-white">
                          <img
                            src={`/images/${product.modelNumber}.jpg`}
                            alt={product.productName}
                            className="w-48 h-48 object-cover mx-auto hidden"
                          />
                          <p className="text-lg font-bold mt-2">
                            {product.makerName && (
                              <div className="mt-2 mb-2 inline-flex items-center rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold text-[#002b5b]">
                                {product.makerName}
                              </div>
                            )}

                            <p className="text-lg font-bold">
                              {product.productName} ({product.modelNumber})
                            </p>
                          </p>
                          <p className="text-gray-700 hidden">
                            {typeof product.price === "number"
                              ? `¥${product.price.toLocaleString()}`
                              : product.price}
                          </p>

                          <ul className="text-sm mt-3 space-y-1 bg-blue-50 rounded-lg p-3">
                            <li>
                              <span className="font-semibold">設置場所：</span>
                              {summary.mountType}
                            </li>
                            <li>
                              <span className="font-semibold">通信方法：</span>
                              {summary.connectivity}
                            </li>
                            <li>
                              <span className="font-semibold">録画方法：</span>
                              {summary.recordingMethod}
                            </li>
                            <li>
                              <span className="font-semibold">撮影範囲：</span>
                              {summary.lensType}
                            </li>
                            <li>
                              <span className="font-semibold">防水防塵：</span>
                              {summary.waterproofDustproof}
                            </li>
                            <li>
                              <span className="font-semibold">画質：</span>
                              {summary.resolution}
                            </li>
                            <li>
                              <span className="font-semibold">暗所性能：</span>
                              {summary.lowLightPerformance}
                            </li>
                            <li>
                              <span className="font-semibold">AI機能：</span>
                              <ul className="mt-1 ml-4 list-disc">
                                {summary.aiFeatures.map((label) => (
                                  <li key={label}>{label}</li>
                                ))}
                              </ul>
                            </li>
                          </ul>

                          <ul className="hidden text-sm mt-2 space-y-1">
                            {questions.map(({ key, question }) => {
                              const selectedValue = selectedOptions[key];
                              const actualValue = product.features[key];
                              const actualValueText = Array.isArray(actualValue)
                                ? actualValue.join("・")
                                : actualValue;

                              const isMismatch = !featureMatches(
                                product.features[key],
                                selectedValue,
                                key,
                                product
                              );

                              return (
                                <li
                                  key={key}
                                  className={isMismatch ? "text-red-500 font-bold" : ""}
                                >
                                  {question}: {actualValueText}
                                </li>
                              );
                            })}
                          </ul>

                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(messageText).then(() => {
                                window.open(lineMessageUrl, "_blank");
                              });
                            }}
                            className="block mt-3 bg-[#00B900] text-white py-1 px-3 rounded hover:brightness-90 text-sm"
                          >
                            LINEでこの商品を相談する
                          </button>
                          <small className="block pl-1 pt-2">
                            スマートフォン以外から閲覧されている場合、LINEでのお問合せが機能しない場合がございます。お問い合わせは以下よりメールでお願いいたします。
                          </small>

                          <button
                            onClick={() => {
                              const productText = generateSingleProductText({
                                product,
                                questions,
                                selections: selectedOptions,
                              });

                              openProductMail({ product, productText });
                            }}
                            className="mt-2 bg-white text-[#002b5b] border border-[#002b5b] py-1 px-3 rounded hover:bg-blue-50 text-sm block"
                          >
                            メールでこの商品を相談する
                          </button>

                          <button
                            onClick={() => {
                              const productText = generateSingleProductText({
                                product,
                                questions,
                                selections: selectedOptions,
                              });

                              const textWithCaseId = caseId
                                ? `受付ID : ${caseId}\n${productText}`
                                : productText;

                              navigator.clipboard.writeText(textWithCaseId).then(() => {
                                alert("この商品をコピーしました！");
                              });
                            }}
                            className="mt-2 bg-[#002b5b] text-white py-1 px-3 rounded hover:bg-blue-700 text-sm block"
                          >
                            この商品情報をコピー
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </>
          )}

          {(filteredProducts.length > 0 || alternativeProducts.length > 0) && (
            <button
              onClick={() => {
                const baseText = generateCopyText({ products: currentList, questions });

                const textWithCaseId = caseId ? `受付ID : ${caseId}\n${baseText}` : baseText;

                navigator.clipboard
                  .writeText(textWithCaseId)
                  .then(() => {
                    alert("商品情報をコピーしました！そのまま問い合わせに使えます。");
                  })
                  .catch(() => {
                    alert("コピーに失敗しました。ブラウザの設定をご確認ください。");
                  });
              }}
              className="mt-6 bg-white text-[#002b5b] py-2 px-4 rounded-lg hover:bg-gray-100"
            >
              全ての商品情報をコピーする
            </button>
          )}

          <h3 className="text-lg font-bold text-white">あなたの選択</h3>
          <ul className="flex flex-wrap gap-4 mt-2 m-auto">
            {questions.map((q) => {
              const selectedValue = selectedOptions[q.key];
              const selectedLabel = getOptionLabel(q, selectedValue);

              return (
                <li
                  key={q.key}
                  className="bg-blue-100 text-sm p-4 rounded-xl w-[45%] text-center shadow"
                >
                  <div className="font-semibold text-[#002b5b]">{q.question}</div>
                  <div className="text-blue-800 font-bold mt-1">{selectedLabel}</div>
                </li>
              );
            })}
          </ul>

          <button
            onClick={() => {
              const textWithCaseId = caseId
                ? `受付ID : ${caseId}\n${selectionText}`
                : selectionText;

              navigator.clipboard.writeText(textWithCaseId).then(() => {
                window.open(lineSelectionUrl, "_blank");
              });
            }}
            className="block mt-8 bg-[#00B900] text-white py-2 px-4 rounded hover:brightness-90"
          >
            選択内容をLINEで相談する
          </button>
          <small className="block pl-1 text-white">
            スマートフォン以外から閲覧されている場合、LINEでのお問合せが機能しない場合がございます。
          </small>

          <button
            onClick={() => {
              const baseText = questions
                .map((q) => {
                  const selectedValue = selectedOptions[q.key];
                  const selectedLabel = getOptionLabel(q, selectedValue);

                  return `${q.question}：${selectedLabel}`;
                })
                .join(" / ");

              const textWithCaseId = caseId ? `受付ID : ${caseId}\n${baseText}` : baseText;

              navigator.clipboard.writeText(textWithCaseId);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="mt-4 text-[#002b5b] bg-white py-2 px-4 rounded hover:bg-[#003d7a] hover:text-white cursor-pointer"
          >
            選択内容をコピー
          </button>

          {copied && <p className="text-sm text-white mt-2">コピーしました！</p>}

          <button
            onClick={reset}
            className="mt-6 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 block"
          >
            最初に戻る
          </button>
        </div>
      )}
    </div>
  );
};

export default Wizard;
